/**
 * CHS - ACO properties bridge
 *
 * The Aco (Product Options & Addons) app injects its hidden
 * `properties[...]` inputs into the FIRST `form[action*="/cart/add"]`
 * in the product section. In this theme that is Shopify's installment
 * banner form (`class="installment"`, rendered by the price block),
 * NOT the real product form that owns the Add to Cart button.
 *
 * Result without this bridge: every add-to-cart posts the bare product
 * with no customization properties.
 *
 * This script mirrors the app's `properties[...]` controls into the
 * real product form as marked clones, and re-syncs them:
 * - whenever the app re-renders its inputs (MutationObserver)
 * - on any form field change (debounced)
 * - synchronously when the buyer presses Add to Cart / submits,
 *   so the values are always fresh at FormData build time.
 *
 * Clones are marked with data-chs-aco-bridge so they are never
 * duplicated and never fight the app's own nodes.
 */

(function () {
  'use strict';

  var BRIDGE_ATTR = 'data-chs-aco-bridge';
  var PROPERTY_SELECTOR =
    'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]';

  function getSections() {
    return Array.from(
      document.querySelectorAll('.shopify-section, section, main')
    );
  }

  function getCartForms(scope) {
    return Array.from(scope.querySelectorAll('form[action*="/cart/add"]'));
  }

  function isRealProductForm(form) {
    return Boolean(form.querySelector('[name="add"]'));
  }

  function getSourceControls(section, destForm) {
    return getCartForms(section)
      .filter(function (form) {
        return form !== destForm && !isRealProductForm(form);
      })
      .reduce(function (controls, form) {
        return controls.concat(
          Array.from(form.querySelectorAll(PROPERTY_SELECTOR)).filter(
            function (control) {
              return !control.hasAttribute(BRIDGE_ATTR) && !control.disabled;
            }
          )
        );
      }, []);
  }

  /*
   * Idempotent sync: only touches the DOM when a name/value actually
   * changed. This matters because the MutationObserver below watches
   * the whole body - unconditional remove/re-add would retrigger the
   * observer every frame and churn forever.
   */
  function syncSection(section) {
    var destForm = getCartForms(section).find(isRealProductForm);

    if (!destForm) {
      return;
    }

    var desired = {};

    getSourceControls(section, destForm).forEach(function (control) {
      if (!control.name) {
        return;
      }

      var existing = destForm.querySelector(
        '[name="' + control.name.replace(/["\\]/g, '\\$&') + '"]:not([' + BRIDGE_ATTR + '])'
      );

      if (!existing) {
        desired[control.name] = control.value;
      }
    });

    var clones = Array.from(destForm.querySelectorAll('[' + BRIDGE_ATTR + ']'));

    clones.forEach(function (clone) {
      if (!(clone.name in desired)) {
        clone.remove();
        return;
      }

      if (clone.value !== desired[clone.name]) {
        clone.value = desired[clone.name];
      }

      delete desired[clone.name];
    });

    Object.keys(desired).forEach(function (name) {
      var clone = document.createElement('input');
      clone.type = 'hidden';
      clone.name = name;
      clone.value = desired[name];
      clone.setAttribute(BRIDGE_ATTR, '1');
      destForm.appendChild(clone);
    });
  }

  function syncAll() {
    getSections().forEach(function (section) {
      if (section.querySelector('form[action*="/cart/add"]')) {
        syncSection(section);
      }
    });
  }

  var queued = false;

  function queueSync() {
    if (queued) {
      return;
    }

    queued = true;
    /* setTimeout, not requestAnimationFrame: rAF pauses in hidden tabs. */
    window.setTimeout(function () {
      queued = false;
      syncAll();
    }, 50);
  }

  function init() {
    syncAll();

    var observer = new MutationObserver(queueSync);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('change', queueSync, true);
    document.addEventListener('input', queueSync, true);

    /*
     * Synchronous re-sync right before the theme builds FormData,
     * so the submitted values can never be stale.
     */
    ['pointerdown', 'mousedown', 'click'].forEach(function (eventName) {
      document.addEventListener(
        eventName,
        function (event) {
          var target = event.target;

          if (!target || !target.closest) {
            return;
          }

          if (
            target.closest(
              '[name="add"], button[type="submit"], .product-form__submit, .shopify-payment-button__button'
            )
          ) {
            syncAll();
          }
        },
        true
      );
    });

    document.addEventListener(
      'submit',
      function () {
        syncAll();
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
